import React, { useState, useCallback, useEffect, useRef, useContext } from 'react';
import { Box, Collapse, Tooltip } from '@mui/material';
import { Typography, colors, IconGraphy } from '@leapeasy/ui-kit';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  formatFlaggedCancelBadgeLabel,
  getFlaggedCancelCountFromState,
  getFlaggedCancelMenuTooltip,
  shouldShowFlaggedCancelMenuBadge,
} from 'utils/flaggedCancellationMenuBadge';
import { shouldShowFlaggedCancellationMenuItem } from 'utils/flaggedCancellationUi';
import { readStoredUser } from 'utils/authSession';
import { resolveMenuItemLink, shouldHideMenuItemForRole } from 'utils/menuAccess';
import { UserContext } from 'context/context';

const COLLAPSED_LEAF_TOOLTIP_PROPS = {
  placement: 'right',
  arrow: true,
  enterDelay: 200,
  disableInteractive: true,
  componentsProps: {
    tooltip: {
      sx: {
        bgcolor: colors.purple[700],
        color: '#fff',
        fontSize: '0.875rem',
        fontWeight: 500,
        padding: '10px 14px',
        borderRadius: '10px',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        boxShadow: '0 10px 24px rgba(0, 0, 0, 0.24)',
        maxWidth: 280,
      },
    },
    arrow: {
      sx: {
        color: colors.purple[700],
      },
    },
  },
};

const CancellationBadge = styled.span`
  flex-shrink: 0;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 999px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  background: ${colors.purple[700]};
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.02em;
  box-sizing: border-box;
`;

const FlaggedCancellationMenuBadge = ({ count }) => (
  <Tooltip
    title={getFlaggedCancelMenuTooltip(count)}
    placement="right"
    arrow
    enterDelay={200}
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: colors.purple[700],
          color: '#fff',
          fontSize: '0.8125rem',
          fontWeight: 400,
          padding: '10px 14px',
          borderRadius: '10px',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          boxShadow: '0 10px 24px rgba(0, 0, 0, 0.24)',
          maxWidth: 320,
        },
      },
      arrow: { sx: { color: colors.purple[700] } },
    }}
  >
    <CancellationBadge aria-label={`${count} flagged cancellations`}>
      {formatFlaggedCancelBadgeLabel(count)}
    </CancellationBadge>
  </Tooltip>
);

const MenuItem = ({
  data,
  isActive,
  hasChildren,
  level,
  onClick,
  collapsed,
  toggleparent,
  flaggedCancelCount,
}) => {
  const fontWeight = isActive ? (hasChildren ? 500 : 600) : 400;
  const hideLabel = Boolean(collapsed && level === 0);
  const showCancellationBadge = shouldShowFlaggedCancelMenuBadge(data, flaggedCancelCount);

  const row = (
    <Box
      component="li"
      sx={{
        cursor: 'pointer',
        padding: '8px 12px',
        borderRadius: '8px',
        color: 'white',
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        position: 'relative',
        opacity: level === 0 ? 1 : 0.75,
        fontWeight: fontWeight,
        flexGrow: 1,
        justifyContent: hideLabel && level === 0 ? 'center' : undefined,

        ...(isActive || (collapsed && toggleparent)
          ? {
              color: colors.purple[900],
              background: colors.black[100],
              opacity: 1,
              fontWeight: Math.min(fontWeight + 100, 600),
            }
          : {
              '&:hover': {
                color: colors.purple[900],
                background: colors.black[100],
                opacity: 0.75,
                fontWeight: Math.min(fontWeight + 100, 600),
              },
            }),
      }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
    >
      {level === 0 && data.icon && (
        <Box display="flex" alignItems="center" sx={{ opacity: 0.6 }}>
          {typeof data.icon === 'string' ? <IconGraphy icon={data.icon} /> : data.icon}
        </Box>
      )}

      <Typography
        variant={level === 0 ? 'body1' : 'body3'}
        style={{
          flexGrow: hideLabel ? 0 : 1,
          userSelect: 'none',
          display: hideLabel ? 'none' : undefined,
          zIndex: collapsed ? -3 : 1,
          transition: 'all 0.4s ease',
          whiteSpace: 'nowrap',
        }}
      >
        {data.name}
      </Typography>

      {showCancellationBadge && <FlaggedCancellationMenuBadge count={flaggedCancelCount} />}

      {hasChildren && !collapsed && (
        <Box display="flex" alignItems="center" sx={{ opacity: 0.6 }}>
          <IconGraphy icon={isActive ? 'Arrow.ExpandLess' : 'Arrow.ExpandMore'} />
        </Box>
      )}
    </Box>
  );

  if (collapsed && level === 0 && !hasChildren) {
    return (
      <Tooltip title={data.name} {...COLLAPSED_LEAF_TOOLTIP_PROPS}>
        {row}
      </Tooltip>
    );
  }

  return row;
};

const getMenuItems = (
  menuArray,
  role,
  level,
  selectedMenu,
  selectedParentMenu,
  onSelectMenu,
  collapsed,
  openCollapsedSubmenuKey,
  onOpenCollapsedSubmenu,
  onScheduleCollapsedSubmenuClose,
  flaggedCancelCount,
) =>
  menuArray
    .filter((item) => !shouldHideMenuItemForRole(role, item.key))
    .filter((item) => shouldShowFlaggedCancellationMenuItem(item.key))
    .map((item) => {
      const childItems = (item.child || []).filter(
        (childMenu) =>
          !shouldHideMenuItemForRole(role, childMenu.key) &&
          shouldShowFlaggedCancellationMenuItem(childMenu.key),
      );

      //Toggle parent Select
      let toggleparent = false;
      if (childItems.length) {
        childItems.forEach((childMenu) => {
          if (selectedMenu.indexOf(childMenu.key) !== -1) {
            toggleparent = true;
          }
        });
      }

      return (
        <Box
          key={item.key}
          className="parentItem"
          onMouseEnter={() => {
            if (collapsed && childItems.length) {
              onOpenCollapsedSubmenu(item.key);
            }
          }}
          onMouseLeave={() => {
            if (collapsed && childItems.length) {
              onScheduleCollapsedSubmenuClose(item.key);
            }
          }}
        >
          <MenuItem
            data={item}
            isActive={item.key === selectedMenu}
            hasChildren={Boolean(childItems.length)}
            level={level}
            onClick={() => onSelectMenu(item.key, item)}
            collapsed={collapsed}
            toggleparent={toggleparent}
            flaggedCancelCount={flaggedCancelCount}
          />

          {!collapsed && childItems.length > 0 && (
            <Collapse
              sx={{ pl: '7px', pt: '4px', position: 'relative' }}
              component="div"
              in={item.key === selectedParentMenu}
              timeout="auto"
              unmountOnExit
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: '18px',
                  top: 0,
                  bottom: '26px',
                  width: '2px',
                  background: 'none',
                  opacity: '0.1',
                }}
              />
              {childItems.map((childMenu, index) => (
                <Box key={childMenu.key} display="flex" alignItems="center" sx={{ gap: '0' }}>
                  <svg width="25" height="40">
                    {index === item.child.length - 1 ? (
                      <path
                        d="M13 11 L13 0 M13 11 Q13 21 23 21"
                        stroke="#F3F3F3"
                        opacity={0.2}
                        strokeWidth="1"
                        fill="none"
                      />
                    ) : (
                      <path
                        d="M13 40 L13 0 M13 11 Q13 21 23 21"
                        stroke="#F3F3F3"
                        opacity={0.2}
                        strokeWidth="1"
                        fill="none"
                      />
                    )}
                  </svg>

                  <MenuItem
                    data={childMenu}
                    isActive={`${item.key}.${childMenu.key}` === selectedMenu}
                    hasChildren={Boolean(childMenu.child)}
                    onClick={() => onSelectMenu(`${item.key}.${childMenu.key}`, childMenu, true)}
                    level={level + 1}
                    flaggedCancelCount={flaggedCancelCount}
                  />
                </Box>
              ))}
            </Collapse>
          )}
          {collapsed && childItems.length > 0 && (
            <div
              className="submenu"
              style={{ display: openCollapsedSubmenuKey === item.key ? 'block' : 'none' }}
              onMouseEnter={() => onOpenCollapsedSubmenu(item.key)}
              onMouseLeave={() => onScheduleCollapsedSubmenuClose(item.key)}
            >
              <div className="submenu-header">{item.name}</div>
              <div className="submenu-divider" aria-hidden />
              <div className="submenu-body">
                {childItems.map((childMenu) => (
                  <MenuItem
                    key={childMenu.key}
                    data={childMenu}
                    isActive={`${item.key}.${childMenu.key}` === selectedMenu}
                    hasChildren={Boolean(childMenu.child)}
                    onClick={() => onSelectMenu(`${item.key}.${childMenu.key}`, childMenu)}
                    level={level + 1}
                    flaggedCancelCount={flaggedCancelCount}
                  />
                ))}
              </div>
            </div>
          )}
        </Box>
      );
    });

export const Menu = ({ data, collapsed }) => {
  const navigate = useNavigate();
  const { closeMobileMenu } = useContext(UserContext) || {};

  const activeMenu = useSelector((state) => state.getIn(['ui', 'menuItem'])) || 'dashboard';
  const flaggedCancelCount = useSelector(getFlaggedCancelCountFromState);

  const [selectedParentMenu, setSelectedParentMenu] = useState();
  const [openCollapsedSubmenuKey, setOpenCollapsedSubmenuKey] = useState(null);
  const collapsedSubmenuCloseTimeoutRef = useRef(null);

  useEffect(() => {
    setSelectedParentMenu(
      activeMenu.includes('.') ? activeMenu.slice(0, activeMenu.indexOf('.')) : null,
    );
  }, [activeMenu]);

  useEffect(() => {
    if (!collapsed) {
      setOpenCollapsedSubmenuKey(null);
      if (collapsedSubmenuCloseTimeoutRef.current) {
        window.clearTimeout(collapsedSubmenuCloseTimeoutRef.current);
        collapsedSubmenuCloseTimeoutRef.current = null;
      }
    }
  }, [collapsed]);

  useEffect(
    () => () => {
      if (collapsedSubmenuCloseTimeoutRef.current) {
        window.clearTimeout(collapsedSubmenuCloseTimeoutRef.current);
        collapsedSubmenuCloseTimeoutRef.current = null;
      }
    },
    [],
  );

  const onOpenCollapsedSubmenu = useCallback((menuKey) => {
    if (collapsedSubmenuCloseTimeoutRef.current) {
      window.clearTimeout(collapsedSubmenuCloseTimeoutRef.current);
      collapsedSubmenuCloseTimeoutRef.current = null;
    }
    setOpenCollapsedSubmenuKey(menuKey);
  }, []);

  const onScheduleCollapsedSubmenuClose = useCallback((menuKey) => {
    if (collapsedSubmenuCloseTimeoutRef.current) {
      window.clearTimeout(collapsedSubmenuCloseTimeoutRef.current);
    }
    collapsedSubmenuCloseTimeoutRef.current = window.setTimeout(() => {
      setOpenCollapsedSubmenuKey((prev) => (prev === menuKey ? null : prev));
      collapsedSubmenuCloseTimeoutRef.current = null;
    }, 180);
  }, []);

  const currentUser = readStoredUser();
  const role = currentUser?.role;

  const onSelectMenu = useCallback(
    (menuKey, item, isChild) => {
      if (item.isExternal) {
        window.open(item.link, '_blank', 'noopener,noreferrer');
        closeMobileMenu?.();
        return;
      }

      if (Boolean(item.child) && !isChild) {
        setSelectedParentMenu((existing) => (existing === menuKey ? null : menuKey));
      } else if (!Boolean(item.child)) {
        setOpenCollapsedSubmenuKey(null);
        let f = 0;
        f = menuKey.includes('.') ? 0 : 1;
        if (!isChild && f === 0) {
          setSelectedParentMenu(menuKey);
        }
        navigate({ pathname: resolveMenuItemLink(role, item), search: '' });
        closeMobileMenu?.();
      }
    },
    [navigate, role, closeMobileMenu],
  );

  return (
    <>
      <Box display="flex" flexDirection="column" gap="12px">
        {getMenuItems(
          data,
          role,
          0,
          activeMenu,
          selectedParentMenu,
          onSelectMenu,
          collapsed,
          openCollapsedSubmenuKey,
          onOpenCollapsedSubmenu,
          onScheduleCollapsedSubmenuClose,
          flaggedCancelCount,
        )}
      </Box>
    </>
  );
};
